/**
 * Porte du NOUVEAU mot de passe maitre (creation du coffre, changement, reinitialisation par la cle de recuperation).
 *
 * `docs/THREAT_MODEL.md` §5 hypothese 4 : Klavox ne protege pas un mot de passe maitre trivial contre la force brute
 * hors ligne. La jauge (`renderMasterPasswordStrengthHint`, `app.ts`) l'annonce a la frappe ; cette porte fait qu'un
 * mot de passe faible n'est accepte qu'apres un consentement explicite, comme la fenetre « mot de passe maitre
 * faible » de Bitwarden. Il n'est pas interdit : la responsabilite reste partagee avec l'utilisateur (meme hypothese).
 *
 * Mesure = celle de la jauge et de la Sante du coffre (`draftPasswordStrength`) : l'e-mail du compte tient lieu
 * d'identifiant, et les sites du produit (« klavox », « santvia ») sont des mots connus -- comme « bitwarden » dans
 * les `user_inputs` de zxcvbn chez Bitwarden. Tout se calcule en memoire ; rien n'est journalise ni transmis.
 *
 * Fuites connues (`assertNewMasterPasswordNotExposed`) : une jauge ne voit pas qu'un mot de passe « fort » circule deja
 * dans les listes des attaquants. Sur demande explicite de l'utilisateur (case « Verifier ce mot de passe dans les
 * fuites connues », comme a l'inscription Bitwarden), le moteur de fuites galaxie l'interroge en k-anonymat : seuls les
 * 5 premiers caracteres de l'empreinte SHA-1 partent, jamais le mot de passe ni l'empreinte complete.
 */
import { BreachCheckError, checkPwnedPasswords, type BreachCheckOptions } from './breach-check.js';
import { draftPasswordStrength } from './vault-health.js';

/** Plancher de l'IHM, identique pour la creation, le changement et la reinitialisation. */
export const MIN_MASTER_PASSWORD_LENGTH = 8;

/** Sites du produit : « klavox » et « santvia », devines d'office par qui vise un coffre Klavox. */
export const MASTER_PASSWORD_PRODUCT_SITES: readonly string[] = ['https://klavox.santvia.eu', 'https://santvia.com'];

/** Robustesse d'un nouveau mot de passe maitre, telle que la jauge l'affiche ; `null` sans mot de passe. */
export function masterPasswordStrength(password: string, email: string): ReturnType<typeof draftPasswordStrength> {
  return draftPasswordStrength(password, email.trim(), MASTER_PASSWORD_PRODUCT_SITES);
}

export class WeakMasterPasswordError extends Error {
  constructor() {
    super(`le nouveau mot de passe maitre doit faire au moins ${MIN_MASTER_PASSWORD_LENGTH} caracteres`);
    this.name = 'WeakMasterPasswordError';
  }
}

/** Mot de passe maitre faible soumis sans le consentement explicite de l'utilisateur. */
export class UnconfirmedWeakMasterPasswordError extends Error {
  readonly bits: number;

  constructor(bits: number) {
    super(
      `mot de passe maitre faible (${Math.round(bits)} bits) : il protege tout le coffre et se devine hors ligne. ` +
        'Choisissez-en un plus fort, ou cochez « Utiliser ce mot de passe faible quand meme ».',
    );
    this.name = 'UnconfirmedWeakMasterPasswordError';
    this.bits = bits;
  }
}

/**
 * Porte de tout NOUVEAU mot de passe maitre, appelee la ou il est fixe : trop court, refuse ; faible, refuse sauf
 * consentement explicite (`acceptWeak`).
 */
export function assertNewMasterPasswordAccepted(password: string, email: string, acceptWeak: boolean): void {
  if (password.length < MIN_MASTER_PASSWORD_LENGTH) throw new WeakMasterPasswordError();
  const strength = masterPasswordStrength(password, email);
  if (strength?.label === 'faible' && !acceptWeak) throw new UnconfirmedWeakMasterPasswordError(strength.bits);
}

/** Mot de passe maitre present dans des fuites connues, soumis sans le consentement explicite de l'utilisateur. */
export class ExposedMasterPasswordError extends Error {
  readonly occurrences: number;

  constructor(occurrences: number) {
    super(
      `mot de passe maitre expose : il figure ${occurrences.toLocaleString('fr-FR')} fois dans des fuites connues, ` +
        'et les attaquants l’essaient en premier. Choisissez-en un autre, ou cochez « Utiliser ce mot de passe expose quand meme ».',
    );
    this.name = 'ExposedMasterPasswordError';
    this.occurrences = occurrences;
  }
}

/**
 * Porte « fuites connues » de tout NOUVEAU mot de passe maitre, appelee la ou il est fixe, avant toute persistance et
 * tout appel au serveur Klavox. `check` = `null` : l'utilisateur n'a pas demande la verification, rien ne part.
 * Expose, refuse sauf consentement explicite (`acceptExposed`, donne apres un premier refus : la case n'existe qu'alors). Service de fuites injoignable : la porte laisse passer
 * (pas de verdict), comme Bitwarden -- une source tierce en panne ne doit pas empecher de creer son coffre.
 */
export async function assertNewMasterPasswordNotExposed(
  password: string,
  check: BreachCheckOptions | null,
  acceptExposed: boolean,
): Promise<void> {
  if (check === null) return;
  let occurrences: number;
  try {
    occurrences = (await checkPwnedPasswords([password], check)).get(password) ?? 0;
  } catch (cause) {
    if (cause instanceof BreachCheckError) return;
    throw cause;
  }
  if (occurrences > 0 && !acceptExposed) throw new ExposedMasterPasswordError(occurrences);
}
